export enum RegexType {
  Alphabetic = 'Alphabetic',
  Numeric = 'Numeric',
  Decimal = 'Decimal',
  Alphanumeric = 'Alphanumeric',
  StartsWithLetter = 'StartsWithLetter',
  StartsWithCapital = 'StartsWithCapital',
  CapitalLetters = 'CapitalLetters',
  WithSpecialChars = 'WithSpecialChars',
  NorthAmericanPhone = 'NorthAmericanPhone',
  EuropeanPhone = 'EuropeanPhone',
  Email = 'Email',
  Url = 'Url',
  PostalCodeCA = 'PostalCodeCA',
  PostalCodeUS = 'PostalCodeUS',
  PostalCodeEU = 'PostalCodeEU',
  CivicNumberCA = 'CivicNumberCA',
  OfficeNumber = 'OfficeNumber',
  Street = "Street",
  StateOrProvince = "StateOrProvince",
  Country = "Country",
  City = "City",
  DateISO = 'DateISO',
  Time24h = 'Time24h',
  IPv4 = 'IPv4',
  IPv6 = 'IPv6',
  HexColor = 'HexColor',
  UUID = 'UUID',
  CreditCard = 'CreditCard',
  StrongPassword = 'StrongPassword',
  CrossValidation = 'CrossValidation'
}

// Type pour les combinaisons de types
export type PropertyTypeCombination =
  | RegexType
  | `${RegexType}`
  | `${RegexType}|${RegexType}`
  | `${RegexType}|${RegexType}|${RegexType}`;

export interface IValidationArgs {
  pattern: string;
  validationErrorMessage: string;
  minLength: number;
  maxLength: number;
}

export class RegexLibrary {
  static readonly validations: Map<RegexType, IValidationArgs> = new Map([
    [RegexType.Alphabetic, {
      pattern: '^[A-ZÉÊËÈÀÂÛËÜa-zéèàêâÉÈùûëü\' ]+$',
      validationErrorMessage: 'messages.field-validation-errors.alphabetic',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.Numeric, {
      pattern: "^\\d+$",
      validationErrorMessage: 'messages.field-validation-errors.numeric',
      minLength: 1,
      maxLength: 10
    }],
    [RegexType.Decimal, {
      pattern: '^\\d+([.,]\\d+)?$',
      validationErrorMessage: 'messages.field-validation-errors.decimal',
      minLength: 1,
      maxLength: 15
    }],
    [RegexType.Alphanumeric, {
      pattern: '^[A-Za-z0-9éèàêâÉÈùûëü \']+$',
      validationErrorMessage: 'messages.field-validation-errors.alphanumeric',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.StartsWithLetter, {
      pattern: '^[A-ZÉÊËÈÀÂÛËÜa-zàâäçéèêëîïôöùûüÿ].*',
      validationErrorMessage: 'messages.field-validation-errors.startswithletter',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.StartsWithCapital, {
      pattern: '^[A-ZÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸ][a-zàâäçéèêëîïôöùûüÿ]+$',
      validationErrorMessage: 'messages.field-validation-errors.startswithcapital',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.CapitalLetters, {
      pattern: '^[A-Z]+$',
      validationErrorMessage: 'messages.field-validation-errors.capitalletters',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.WithSpecialChars, {
      pattern: '^[\\w\\s!@#$%^&*(),.?+-_!/\\":{}|<>-]+$',
      validationErrorMessage: 'messages.field-validation-errors.withspecialchars',
      minLength: 0,
      maxLength: 255
    }],
    [RegexType.NorthAmericanPhone, {
      pattern: '^(?:\\+1[-.\\s]?)?(?:\\(?([2-9]\\d{2})\\)?[-.\\s]?)?([2-9]\\d{2})[-.\\s]?(\\d{4})$',
      validationErrorMessage: 'messages.field-validation-errors.northamericanphone',
      minLength: 10,
      maxLength: 25
    }],
    [RegexType.EuropeanPhone, {
      pattern: '^\\+?\\d{1,4}?[-.\\s]?\\(?\\d{1,4}?\\)?[-.\\s]?\\d{3,4}[-.\\s]?\\d{3,4}$',
      validationErrorMessage: 'messages.field-validation-errors.europeanphone',
      minLength: 10,
      maxLength: 25
    }],
    [RegexType.Email, {
      pattern: '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$',
      validationErrorMessage: 'messages.field-validation-errors.email',
      minLength: 6,
      maxLength: 320
    }],
    [RegexType.Url, {
      pattern: '^(https?:\/\/)?([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,}(:\\d+)?(\\/[^\\s]*)?$',
      validationErrorMessage: 'messages.field-validation-errors.url',
      minLength: 10,
      maxLength: 2048
    }],
    [RegexType.PostalCodeCA, {
      pattern: '^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$',
      validationErrorMessage: 'messages.field-validation-errors.postalcodeca',
      minLength: 6,
      maxLength: 10
    }],
    [RegexType.PostalCodeUS, {
      pattern: '^(?!00000)(\\d{5})(-(?!0000)\\d{4})?$',
      validationErrorMessage: 'messages.field-validation-errors.postalcodeus',
      minLength: 5,
      maxLength: 10
    }],
    [RegexType.PostalCodeEU, {
      pattern: '^\\d{4,5}$',
      validationErrorMessage: 'messages.field-validation-errors.postalcodeeu',
      minLength: 4,
      maxLength: 5
    }],
    [RegexType.CivicNumberCA, {
      pattern: '^\\d{1,6}[A-Za-z]?(?: ?[A-Za-z0-9]{0,3})?$',
      validationErrorMessage: 'messages.field-validation-errors.civicnumberca',
      minLength: 1,
      maxLength: 10
    }],
    [RegexType.OfficeNumber, {
      pattern: '^\\d{1,6}[A-Za-z0-9]?(?: ?[A-Za-z0-9])?$',
      validationErrorMessage: 'messages.field-validation-errors.officenumber',
      minLength: 1,
      maxLength: 10
    }],
    [RegexType.Street, {
      pattern: '^[A-Za-z0-9 \']+$',
      validationErrorMessage: 'messages.field-validation-errors.street',
      minLength: 1,
      maxLength: 100
    }],
    [RegexType.StateOrProvince, {
      pattern: '^[A-Za-zÀ-ÖØ-öø-ÿĀ-žÑñáéíóúÁÉÍÓÚâêîôûÂÊÎÔÛäëïöüÄËÏÖÜãõÃÕçÇ\'’- ]$',
      validationErrorMessage: 'messages.field-validation-errors.stateorprovince',
      minLength: 1,
      maxLength: 60
    }],
    [RegexType.Country, {
      pattern: '^[A-Za-zÀ-ÖØ-öø-ÿĀ-žÑñáéíóúÁÉÍÓÚâêîôûÂÊÎÔÛäëïöüÄËÏÖÜãõÃÕçÇ\'’- ]$',
      validationErrorMessage: 'messages.field-validation-errors.country',
      minLength: 1,
      maxLength: 20
    }],
    [RegexType.City, {
      pattern: '^[A-Za-zÀ-ÖØ-öø-ÿĀ-žÑñáéíóúÁÉÍÓÚâêîôûÂÊÎÔÛäëïöüÄËÏÖÜãõÃÕçÇ\'’- ]$',
      validationErrorMessage: 'messages.field-validation-errors.city',
      minLength: 1,
      maxLength: 60
    }],
    [RegexType.DateISO, {
      pattern: '^\\d{4}-\\d{2}-\\d{2}$',
      validationErrorMessage: 'messages.field-validation-errors.dateiso',
      minLength: 10,
      maxLength: 10
    }],
    [RegexType.Time24h, {
      pattern: '^([01]\\d|2[0-3]):[0-5]\\d$',
      validationErrorMessage: 'messages.field-validation-errors.time24h',
      minLength: 5,
      maxLength: 5
    }],
    [RegexType.IPv4, {
      pattern: '^(\\d{1,3}\\.){3}\\d{1,3}$',
      validationErrorMessage: 'messages.field-validation-errors.ipv4',
      minLength: 7,
      maxLength: 15
    }],
    [RegexType.IPv6, {
      pattern: '^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$',
      validationErrorMessage: 'messages.field-validation-errors.ipv6',
      minLength: 15,
      maxLength: 39
    }],
    [RegexType.HexColor, {
      pattern: '^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$',
      validationErrorMessage: 'messages.field-validation-errors.hexcolor',
      minLength: 3,
      maxLength: 7
    }],
    [RegexType.UUID, {
      pattern: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
      validationErrorMessage: 'messages.field-validation-errors.uuid',
      minLength: 36,
      maxLength: 36
    }],
    [RegexType.CreditCard, {
      pattern: '^\\d{4}[- ]?\\d{4}[- ]?\\d{4}[- ]?\\d{4}$',
      validationErrorMessage: 'messages.field-validation-errors.creditcard',
      minLength: 16,
      maxLength: 19
    }],
    [RegexType.StrongPassword, {
      pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[_#?%$&()=|@!*+.,:~^-])[A-Za-z0-9À-ÖØ-öø-ÿ_#?%$&()=|@!*+.,:~^-]{8,50}$',
      validationErrorMessage: 'messages.field-validation-errors.strongpassword',
      minLength: 8,
      maxLength: 50
    }]
  ]);


  static getPattern(type: RegexType): string | undefined {
    return this.validations.get(type)?.pattern;
  }

  static getValidationErrorMessage(type: string): string | undefined {
    let baseMessage = this.validations.get(type as RegexType)?.validationErrorMessage;
     return baseMessage; 
  }

  static getDefaultLengths(type: RegexType): { min?: number; max?: number } {
    const validation = this.validations.get(type);
    return validation ? { min: validation.minLength, max: validation.maxLength } : {};
  }
}
